var _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; };

var _slicedToArray = function () { function sliceIterator(arr, i) { var _arr = []; var _n = true; var _d = false; var _e = undefined; try { for (var _i = arr[Symbol.iterator](), _s; !(_n = (_s = _i.next()).done); _n = true) { _arr.push(_s.value); if (i && _arr.length === i) break; } } catch (err) { _d = true; _e = err; } finally { try { if (!_n && _i["return"]) _i["return"](); } finally { if (_d) throw _e; } } return _arr; } return function (arr, i) { if (Array.isArray(arr)) { return arr; } else if (Symbol.iterator in Object(arr)) { return sliceIterator(arr, i); } else { throw new TypeError("Invalid attempt to destructure non-iterable instance"); } }; }();

function _toConsumableArray(arr) { if (Array.isArray(arr)) { for (var i = 0, arr2 = Array(arr.length); i < arr.length; i++) { arr2[i] = arr[i]; } return arr2; } else { return Array.from(arr); } }

import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { injectIntl } from 'react-intl';
import AccordionWidget from '../AccordionWidget';
import Button from '../../components/Button';
import normalize from './normalize';
import validate from './validate';
import messages from './messages';
import Timespan from './Timespan';
import getByPath from 'lodash/get';
import marked from 'marked';

var ResponsibleGaming = function ResponsibleGaming(_ref) {
  var limits = _ref.limits,
      content = _ref.content,
      onSubmit = _ref.onSubmit,
      onDeclineLimit = _ref.onDeclineLimit,
      onConfirmLimit = _ref.onConfirmLimit,
      limitIncreaseInfo = _ref.limitIncreaseInfo,
      jurisdiction = _ref.jurisdiction,
      currency = _ref.currency,
      onChangeLimits = _ref.onChangeLimits,
      isLoading = _ref.isLoading,
      className = _ref.className,
      hideSubmit = _ref.hideSubmit;

  var _useState = useState(limits || []),
      _useState2 = _slicedToArray(_useState, 2),
      localLimits = _useState2[0],
      setLocalLimits = _useState2[1];

  useEffect(function () {
    return setLocalLimits(limits || []);
  }, [limits]);

  var normalizedLimits = normalize(localLimits, content);

  useEffect(function () {
    onChangeLimits && onChangeLimits(localLimits);
  }, [localLimits]);

  /**
   * Validate the normalized limits
   */
  var getValidationState = function getValidationState(type) {
    return function (timespan) {
      return validate({
        normalizedLimits: normalizedLimits,
        jurisdiction: jurisdiction,
        limitIncreaseInfo: limitIncreaseInfo,
        type: type,
        timespan: timespan
      });
    };
  };

  /**
   * Get alerts from the validation state
   */
  var getAlerts = function getAlerts(type) {
    return function (timespans) {
      var values = _extends({}, limitIncreaseInfo);
      var alerts = timespans.map(function (timespan) {
        return getValidationState(type)(timespan);
      }).map(function (validationState) {
        return Object.keys(validationState).filter(function (rule) {
          return typeof validationState[rule] === 'boolean' && validationState[rule];
        });
      }).reduce(function (rules, rule) {
        return [].concat(_toConsumableArray(rules), _toConsumableArray(rule));
      }, []).reduce(function (rules, rule) {
        return [].concat(_toConsumableArray(rules.filter(function (r) {
          return r !== rule;
        })), [rule]);
      }, []).map(function (rule) {
        return messages.get('alert_message.warning.responsible-gaming.rule.' + type + '.' + rule, values) || messages.get('alert_message.warning.responsible-gaming.rule.' + rule, values);
      }).filter(function (rule) {
        return rule;
      });
      return alerts;
    };
  };

  var onSubmitLimits = function onSubmitLimits(type, product) {
    return function (limits) {
      return typeof onSubmit === 'function' && onSubmit({
        type: type,
        product: product,
        limits: limits,
        validationState: getValidationState(type)()
      });
    };
  };

  /**
   * Add a new limit with status "NewLimit"
   * There can only be one limit with status "NewLimit" for each type/timespan
   */
  var onChangeLimit = function onChangeLimit(type, product) {
    return function (timespan) {
      return function (amount) {
        var newLimit = {
          Id: getByPath(normalizedLimits, '[' + type + '].timespans[' + timespan + '].currentLimit.Id'),
          Type: type,
          Product: product,
          Timespan: timespan,
          Amount: amount,
          Status: 'NewLimit'
        };
        var limits = localLimits.filter(function (l) {
          return !(l.Type === newLimit.Type && l.Product === newLimit.Product && l.Timespan === newLimit.Timespan && l.Status === newLimit.Status);
        });
        setLocalLimits([].concat(_toConsumableArray(limits), [newLimit]));
      };
    };
  };

  return React.createElement(
    'div',
    { className: cn('ResponsibleGamingWidget', className) },
    React.createElement(
      AccordionWidget,
      {
        initialExpandedIndex: Object.values(normalizedLimits).length === 1 ? 0 : null },
      Object.values(normalizedLimits).map(function (normalizedLimits, i) {
        return React.createElement(
          'div',
          { className: 'ResponsibleGamingWidget__limit-item', key: i },
          React.createElement(LimitHeader, {
            title: messages.get('header.responsible-gaming.' + normalizedLimits.type + normalizedLimits.product),
            tooltipContent: marked(normalizedLimits.content),
            tooltipOpen: normalizedLimits.tooltipOpen
          }),
          React.createElement(LimitContent, _extends({
            onSubmitLimits: onSubmitLimits(normalizedLimits.type, normalizedLimits.product),
            onChangeLimit: onChangeLimit(normalizedLimits.type, normalizedLimits.product),
            hideSubmit: hideSubmit,
            onConfirmLimit: onConfirmLimit,
            onDeclineLimit: onDeclineLimit,
            getValidationState: getValidationState(normalizedLimits.type),
            getAlerts: getAlerts(normalizedLimits.type),
            isLoading: isLoading,
            currency: currency
          }, normalizedLimits))
        );
      })
    )
  );
};

var LimitHeader = function LimitHeader(_ref2) {
  var title = _ref2.title;
  return React.createElement(
    'div',
    { className: 'ResponsibleGamingWidget__limit-header' },
    title && React.createElement(
      'div',
      { className: 'ResponsibleGamingWidget__limit-header-title' },
      title
    )
  );
};

var LimitContent = function LimitContent(_ref3) {
  var timespans = _ref3.timespans,
      onSubmitLimits = _ref3.onSubmitLimits,
      onChangeLimit = _ref3.onChangeLimit,
      onConfirmLimit = _ref3.onConfirmLimit,
      onDeclineLimit = _ref3.onDeclineLimit,
      getValidationState = _ref3.getValidationState,
      getAlerts = _ref3.getAlerts,
      isLoading = _ref3.isLoading,
      currency = _ref3.currency,
      values = _ref3.values,
      valueType = _ref3.valueType,
      hideRemainingLimit = _ref3.hideRemainingLimit,
      showCurrentValue = _ref3.showCurrentValue,
      hideOther = _ref3.hideOther,
      hideSubmit = _ref3.hideSubmit;

  var formIsValid = Object.keys(timespans).every(function (t) {
    return getValidationState(t).isValid();
  }) && !getValidationState()['limits-are-pristine'];

  var alerts = getAlerts(Object.keys(timespans));

  var onSubmit = function onSubmit(e) {
    e.preventDefault();
    var limits = Object.values(timespans).filter(function (t) {
      return !t.pendingDeleteLimit && !t.pendingActivateLimit;
    }).map(function (t) {
      return t.newLimit || t.currentLimit;
    }).filter(function (t) {
      return t;
    });
    return formIsValid ? onSubmitLimits(limits) : Promise.resolve();
  };

  return React.createElement(
    'form',
    {
      className: cn('ResponsibleGamingWidget__limit-content', {
        'ResponsibleGamingWidget__limit-content--valid': formIsValid
      }),
      onSubmit: onSubmit },
    alerts.map(function (a, i) {
      return React.createElement(
        'div',
        { key: i, className: 'ResponsibleGamingWidget__alert' },
        a
      );
    }),
    React.createElement(AdminLimits, {
      timespans: timespans,
      valueType: valueType,
      currency: currency
    }),
    Object.values(timespans).map(function (t) {
      return React.createElement(Timespan, _extends({
        key: t.timespan,
        title: Object.values(timespans).length > 1 && messages.get('header.responsible-gaming.timespan.' + t.timespan),
        onChangeLimit: onChangeLimit(t.timespan),
        onResetLimit: function onResetLimit() {
          return onSubmitLimits([t.currentLimit]);
        },
        onConfirmLimit: onConfirmLimit,
        onDeclineLimit: onDeclineLimit,
        onPickLimit: function onPickLimit(id) {
          return onSubmitLimits(t.pickLimits.filter(function (l) {
            return l.Id !== id;
          }).map(function (l) {
            return _extends({}, l, { Amount: 0 });
          }));
        },
        validationState: getValidationState(t.timespan),
        currency: currency,
        values: values,
        valueType: valueType,
        hideRemainingLimit: hideRemainingLimit,
        showCurrentValue: showCurrentValue,
        hideOther: hideOther
      }, t));
    }),
    !hideSubmit && React.createElement(
      Button,
      {
        type: 'submit',
        disabled: !formIsValid,
        className: 'button primary wide',
        isLoading: isLoading },
      messages.get('action.responsible-gaming.save-limits')
    )
  );
};

var AdminLimits = injectIntl(function (_ref4) {
  var timespans = _ref4.timespans,
      valueType = _ref4.valueType,
      currency = _ref4.currency,
      intl = _ref4.intl;

  var adminLimits = Object.values(timespans).map(function (l) {
    return l.adminLimit;
  }).filter(function (l) {
    return l;
  }).map(function (l) {
    return '<li>' + messages.get('label.responsible-gaming.admin-limits-row', {
      intl: intl,
      Timespan: l.Timespan,
      Amount: messages.getValue(l.Amount, valueType, currency, intl),
      Product: l.Product
    }) + '</li>';
  }).join('');
  if (adminLimits.length === 0) return React.createElement('div', { className: 'AdminLimits' });
  return React.createElement(
    'div',
    { className: 'AdminLimits' },
    messages.get('alert_message.warning.responsible-gaming.admin-limits', {
      adminLimits: marked('<ul>' + adminLimits + '</ul>')
    })
  );
});

ResponsibleGaming.propTypes = {
  /** Array of limits */
  limits: PropTypes.array,
  /** Limits content. Each item in the items array is a section representing a limit type */
  content: PropTypes.shape({
    identifier: PropTypes.string,
    items: PropTypes.array
  }),
  /** Function called when submitting limits */
  onSubmit: PropTypes.func,
  /** Function called when declining a limit */
  onDeclineLimit: PropTypes.func,
  /** Function called when confirming a limit */
  onConfirmLimit: PropTypes.func,
  /** Object containing info about limit increase state. Used to determine validation state for ES players. */
  limitIncreaseInfo: PropTypes.object,
  /** Jurisdiction for the player. Used for applying validation rules depending on jurisdiction. */
  jurisdiction: PropTypes.string,
  /** The currency that should be displayed for limits whose value type is 'currency'. */
  currency: PropTypes.string,
  /** Shows a loading spinner. */
  isLoading: PropTypes.bool
};

export default ResponsibleGaming;

export { normalize, validate, messages };